import React from 'react';

export const CHART_TICK_FONT_SIZE = 14;
export const CHART_TICK_FILL = 'rgba(0,75,77,0.65)';
export const CHART_TICK_FONT_FAMILY = 'NotoSans, sans-serif';
export const chartAxisTickClass = 'font-sans text-[14px] leading-none text-portal-teal/65';

/** One date stays. Two dates are the start and the end. A longer series adds the centre date. */
export function chartDateTicks(dates: string[]): string[] {
  if (dates.length <= 2) {
    return [...dates];
  }

  const middle = dates[Math.round((dates.length - 1) / 2)];
  const last = dates[dates.length - 1];
  if (middle === dates[0] || middle === last) {
    return [dates[0], last];
  }

  return [dates[0], middle, last];
}

export function formatChartTickValue(value?: string | number): string {
  if (value == null) {
    return '';
  }

  if (typeof value === 'number') {
    return String(value);
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const date = new Date(`${value}T00:00:00Z`);
    if (!Number.isNaN(date.getTime())) {
      return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
    }
  }

  return value;
}

export function ChartAxisTick({
  x = 0,
  y = 0,
  dy,
  payload,
  textAnchor = 'middle',
  html,
}: {
  x?: number;
  y?: number;
  dy?: number;
  payload?: { value?: string | number };
  textAnchor?: string;
  html?: boolean;
}): React.ReactElement {
  const label = formatChartTickValue(payload?.value);
  if (html) {
    return <span className={`${chartAxisTickClass} whitespace-nowrap`}>{label}</span>;
  }

  const baseline = dy ?? (textAnchor === 'end' ? 4 : 12);
  return (
    <text
      x={x}
      y={y}
      dy={baseline}
      textAnchor={textAnchor}
      fill={CHART_TICK_FILL}
      fontSize={CHART_TICK_FONT_SIZE}
      fontFamily={CHART_TICK_FONT_FAMILY}
    >
      {label}
    </text>
  );
}

export function ChartDateAxis({
  dates,
  columnGap = 3,
}: {
  dates: string[];
  columnGap?: number;
}): React.ReactElement | null {
  if (dates.length === 0) {
    return null;
  }

  const marked = new Set(chartDateTicks(dates));
  return (
    <div
      className="mt-1 grid w-full"
      style={{
        gridTemplateColumns: `repeat(${dates.length}, minmax(0, 1fr))`,
        columnGap,
      }}
      aria-hidden="true"
    >
      {dates.map((date, index) => {
        const isTick = marked.has(date);
        const align = !isTick
          ? ''
          : index === dates.length - 1
            ? 'text-right'
            : 'text-left';
        return (
          <div key={`${date}-${index}`} className={`min-w-0 overflow-visible ${align}`}>
            {isTick ? <ChartAxisTick html payload={{ value: date }} /> : null}
          </div>
        );
      })}
    </div>
  );
}
